import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Save, Trash2, Search, Loader2, ListOrdered, AlertTriangle, TrendingDown } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { PricingChannelSelect } from '../../../components/PricingChannelSelect';
import { MrpSetCell, rowSetIssue } from '../../../components/comboV2/MrpSetCell';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Combo V2 — Bulk Assembly Planner.
 * Matrix UI: rows = (Design + Shade + Description), cols = sizes 36–52, cells = qty.
 * Save → backend creates one assembly session per non-zero cell + 120-cap pick slips.
 */
export default function ComboV2Planner() {
  const navigate = useNavigate();
  const [sizes, setSizes] = useState([]);
  const [optionsByKey, setOptionsByKey] = useState({});  // "design|shade" -> {sizes_available}
  const [allOptions, setAllOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [planName, setPlanName] = useState('');
  const [pricingType, setPricingType] = useState('TN');
  const [rows, setRows] = useState([
    { design_name: '', shade: '', description: '', sizes: {}, _query: '',
      customer_barcode: '', customer_mrp: null, rate_section: null,
      _barcodeOptions: [], _loadingBarcodes: false },
  ]);
  const [saving, setSaving] = useState(false);
  const [savedPlanId, setSavedPlanId] = useState(null);
  // 2026-05-13 — stock-aware planner result (skipped + adjusted cells).
  const [planResult, setPlanResult] = useState(null);   // { plan_id, sessions_count, slips_count, skipped_cells, qty_adjustments }
  const [resultDialogOpen, setResultDialogOpen] = useState(false);

  // 2026-05-15 — Stock diagnostic + heal flow. Lets ops self-serve when
  // bottleneck shows '0 pcs' but Excel says stock is physically there
  // (orphan ALLOCATED / PICKED_FOR_COMBO_V2 from cancelled plans).
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagSku, setDiagSku] = useState('');
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const [diagHealing, setDiagHealing] = useState(false);

  const openDiagnostic = async (skuCode) => {
    if (!skuCode) return;
    setDiagSku(skuCode);
    setDiagOpen(true);
    setDiagData(null);
    setDiagLoading(true);
    try {
      const res = await axios.get(`${API}/api/combo-v2/admin/diagnose-stock`, {
        params: { sku_code: skuCode },
        withCredentials: true,
      });
      setDiagData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load stock diagnostic');
      setDiagOpen(false);
    } finally {
      setDiagLoading(false);
    }
  };

  const runHeal = async (dryRun) => {
    if (!diagSku) return;
    setDiagHealing(true);
    try {
      const res = await axios.post(
        `${API}/api/combo-v2/admin/heal-orphan-allocations`,
        { sku_code: diagSku, dry_run: dryRun },
        { withCredentials: true },
      );
      if (dryRun) {
        toast.info(res.data.message || `Would release ${res.data.orphan_bundles_found} bundle(s).`);
      } else {
        toast.success(`Released ${res.data.updated} bundle(s) back to STORED.`);
      }
      // Refresh diagnostic.
      await openDiagnostic(diagSku);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Heal failed');
    } finally {
      setDiagHealing(false);
    }
  };

  // Picker dropdown state — anchorRect tracks the input's screen position so the
  // portal can render the dropdown OUTSIDE the table's overflow boundary.
  const [openPickerIdx, setOpenPickerIdx] = useState(null);
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const inputRefs = useRef({});

  const openPicker = (idx) => {
    const el = inputRefs.current[idx];
    if (el) {
      const r = el.getBoundingClientRect();
      setPickerAnchor({ left: r.left, top: r.bottom + 4, width: r.width });
    }
    setOpenPickerIdx(idx);
  };

  // Reposition picker on scroll/resize
  useEffect(() => {
    if (openPickerIdx === null) return;
    const reposition = () => {
      const el = inputRefs.current[openPickerIdx];
      if (el) {
        const r = el.getBoundingClientRect();
        setPickerAnchor({ left: r.left, top: r.bottom + 4, width: r.width });
      }
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [openPickerIdx]);

  useEffect(() => {
    loadOptions();
  }, []);

  const loadOptions = async () => {
    try {
      setLoadingOptions(true);
      const tk = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/api/combo-v2/plans/lookup/options`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setSizes(res.data.sizes || []);
      const opts = res.data.rows || [];
      setAllOptions(opts);
      const map = {};
      for (const o of opts) {
        // Key includes description so H/S vs F/S variants are distinguishable.
        map[`${o.design_name}||${o.shade}||${o.description || ''}`] = o;
      }
      setOptionsByKey(map);
    } catch (e) {
      toast.error('Failed to load combo options');
    } finally {
      setLoadingOptions(false);
    }
  };

  const updateRow = (idx, patch) => {
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const setCell = (idx, size, val) => {
    const v = val === '' ? '' : Math.max(0, parseInt(val, 10) || 0);
    setRows(prev =>
      prev.map((r, i) => (i === idx ? { ...r, sizes: { ...r.sizes, [size]: v } } : r))
    );
  };

  const addRow = () => {
    setRows(prev => [...prev, {
      design_name: '', shade: '', description: '', sizes: {}, _query: '',
      customer_barcode: '', customer_mrp: null, rate_section: null,
      _barcodeOptions: [], _loadingBarcodes: false,
    }]);
  };

  const removeRow = idx => {
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const fetchBarcodesForRow = async (idx, design_name, shade, description, pt = pricingType) => {
    if (!design_name || !shade) return;
    updateRow(idx, { _loadingBarcodes: true, _barcodeOptions: [],
                     customer_barcode: '', customer_mrp: null, rate_section: null });
    try {
      const tk = localStorage.getItem('session_token');
      // 2026-05-18 (follow-up) — fetch DISTINCT MRPs for the combo.
      // Operator picks MRP, not barcode (the matrix collapses size into
      // columns; per-size barcode is resolved downstream at slip-create
      // from rates_master + the picked MRP).
      const res = await axios.get(`${API}/api/combo-v2/plans/lookup/mrps-for-combo`, {
        headers: { Authorization: `Bearer ${tk}` },
        params: { design_name, shade, description: description || '', pricing_type: pt },
      });
      const opts = res.data.options || [];
      const patch = { _barcodeOptions: opts, _loadingBarcodes: false };
      // Auto-fill when there is exactly one MRP (common case).
      if (opts.length === 1) {
        patch.customer_mrp = opts[0].mrp;
      }
      updateRow(idx, patch);
    } catch (e) {
      updateRow(idx, { _loadingBarcodes: false });
    }
  };

  // Per-size component-stock breakdown for the row's combo. Populates the
  // stock sub-row (Possible qty + each component's available SFG pieces).
  const fetchComponentStock = async (idx, design_name, shade, description, customer_mrp, pt = pricingType, rate_section = null) => {
    if (!design_name || !shade) return;
    updateRow(idx, { _loadingStock: true });
    try {
      const tk = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/api/combo-v2/plans/lookup/component-stock`, {
        headers: { Authorization: `Bearer ${tk}` },
        params: {
          design_name, shade, description: description || '',
          ...(customer_mrp != null ? { customer_mrp } : {}),
          ...(rate_section != null ? { rate_section } : {}),
          pricing_type: pt,
        },
      });
      updateRow(idx, {
        _componentStock: res.data?.sizes || {},
        _rowPossible: (typeof res.data?.row_total_possible === 'number' ? res.data.row_total_possible : null),
        _loadingStock: false,
      });
    } catch (e) {
      updateRow(idx, { _loadingStock: false });
    }
  };

  // Fill every size of a row with its per-size Possible qty (fast max-planning).
  // NOTE: per-size Possibles each see the full shared pool, so their SUM can
  // exceed the row's true "Max buildable"; Generate applies the real capping.
  const fillAllPossible = (idx) => {
    setRows(prev => prev.map((row, i) => {
      if (i !== idx) return row;
      const stock = row._componentStock || {};
      const newSizes = { ...row.sizes };
      for (const [sz, info] of Object.entries(stock)) {
        newSizes[sz] = info?.possible_qty || 0;
      }
      return { ...row, sizes: newSizes };
    }));
  };

  const pickOption = (idx, opt) => {
    updateRow(idx, {
      design_name: opt.design_name,
      shade: opt.shade,
      description: opt.description || '',
      _query: '',
      customer_barcode: '',
      customer_mrp: null, rate_section: null,
      _barcodeOptions: [],
      _componentStock: null,
      _rowPossible: null,
    });
    setOpenPickerIdx(null);
    // Async fetch MRPs for the combo just picked.
    fetchBarcodesForRow(idx, opt.design_name, opt.shade, opt.description || '');
    // Async fetch per-size component stock (MRP-independent; refined on MRP pick).
    fetchComponentStock(idx, opt.design_name, opt.shade, opt.description || '', null);
  };

  // Pricing channel changed → every picked row re-reads its MRPs (and stock) for that channel.
  const changeChannel = (pt) => {
    setPricingType(pt);
    rows.forEach((r, idx) => {
      if (!r.design_name || !r.shade) return;
      fetchBarcodesForRow(idx, r.design_name, r.shade, r.description || '', pt);
      fetchComponentStock(idx, r.design_name, r.shade, r.description || '', null, pt);
    });
  };

  const onSelectMrp = (idx, mrpStr) => {
    const mrp = mrpStr === '' ? null : parseFloat(mrpStr);
    updateRow(idx, { customer_mrp: mrp, rate_section: null });
    const r = rows[idx];
    if (r) fetchComponentStock(idx, r.design_name, r.shade, r.description || '', mrp);
  };

  // MRP with several barcode sets (Rates Master section) → operator picks the set.
  const onSelectSet = (idx, sec) => {
    updateRow(idx, { rate_section: sec });
    const r = rows[idx];
    if (r) fetchComponentStock(idx, r.design_name, r.shade, r.description || '', r.customer_mrp, pricingType, sec);
  };

  const filteredOptions = (q) => {
    if (!q) return allOptions.slice(0, 50);
    const ql = q.toLowerCase();
    return allOptions
      .filter(o => `${o.design_name} ${o.shade} ${o.description}`.toLowerCase().includes(ql))
      .slice(0, 50);
  };

  // Per-row allowed sizes from master (design+shade+description match).
  const allowedSizesForRow = (r) => {
    const exact = optionsByKey[`${r.design_name}||${r.shade}||${r.description || ''}`];
    if (exact) return new Set(exact.sizes_available || []);
    // Fallback for legacy rows that don't have description set
    const fallback = Object.entries(optionsByKey).find(([k]) =>
      k.startsWith(`${r.design_name}||${r.shade}||`),
    );
    return fallback ? new Set(fallback[1].sizes_available || []) : null;
  };

  // Sort sizes numerically where possible, lex otherwise (walk-order).
  const sortSizes = (arr) => {
    const distinct = Array.from(new Set(arr));
    distinct.sort((a, b) => {
      const na = parseFloat(a);
      const nb = parseFloat(b);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
      return String(a).localeCompare(String(b));
    });
    return distinct;
  };

  // Size columns rendered in the matrix.
  //
  // The combo BOM master is the authoritative source for which sizes exist
  // for a given design+shade — so the column list is the UNION of
  // `sizes_available` for every row whose design+shade are already chosen.
  //
  // If the operator hasn't picked any design yet, fall back to the
  // backend's global `sizes` list so the matrix is visible (otherwise
  // they'd stare at a column-less table on first load).
  const displayedSizes = useMemo(() => {
    const fromRows = [];
    for (const r of rows) {
      const allowed = allowedSizesForRow(r);
      if (allowed && allowed.size > 0) {
        for (const sz of allowed) fromRows.push(sz);
      }
    }
    if (fromRows.length === 0) {
      return sortSizes(sizes);
    }
    return sortSizes(fromRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sizes, optionsByKey]);

  const totals = useMemo(() => {
    const perCol = {};
    let grand = 0;
    for (const sz of displayedSizes) perCol[sz] = 0;
    for (const r of rows) {
      for (const sz of displayedSizes) {
        const v = parseInt(r.sizes?.[sz] || 0, 10) || 0;
        perCol[sz] += v;
        grand += v;
      }
    }
    return { perCol, grand };
  }, [rows, displayedSizes]);

  const rowSum = r =>
    displayedSizes.reduce((acc, sz) => acc + (parseInt(r.sizes?.[sz] || 0, 10) || 0), 0);

  const handleSave = async () => {
    const valid = rows.filter(r => r.design_name && r.shade && rowSum(r) > 0);
    if (!valid.length) {
      toast.error('Add at least one row with design, shade and qty');
      return;
    }
    const setIssue = rows.map((r, i) => (valid.includes(r) ? [i, rowSetIssue(r)] : [i, null])).find(([, m]) => m);
    if (setIssue) {
      toast.error(`Row ${setIssue[0] + 1}: ${setIssue[1]}`);
      return;
    }
    try {
      setSaving(true);
      const tk = localStorage.getItem('session_token');
      const res = await axios.post(
        `${API}/api/combo-v2/plans`,
        {
          plan_name: planName || null,
          pricing_type: pricingType,
          rows: valid.map(r => ({
            design_name: r.design_name,
            shade: r.shade,
            description: r.description || '',
            sizes: Object.fromEntries(
              Object.entries(r.sizes).filter(([_, v]) => v && parseInt(v, 10) > 0)
            ),
            // 2026-05-18 (follow-up) — barcode is resolved per-size at
            // slip-create from rates_master + customer_mrp; operator
            // only picks MRP here.
            customer_mrp: r.customer_mrp ?? null,
            rate_section: r.rate_section ?? null,
          })),
        },
        { headers: { Authorization: `Bearer ${tk}` } }
      );
      setSavedPlanId(res.data.plan_id);
      setPlanResult(res.data);
      const skipped = res.data.skipped_cells || [];
      const adjusted = res.data.qty_adjustments || [];
      if (skipped.length || adjusted.length) {
        // Open the bottleneck dialog so the operator sees exactly which
        // combos were skipped / capped due to component shortages.
        setResultDialogOpen(true);
        toast.success(
          `Plan created: ${res.data.sessions_count} session(s), ${res.data.slips_count} pick slip(s)`
          + (adjusted.length ? ` • ${adjusted.length} qty adjustment(s)` : '')
          + (skipped.length ? ` • ${skipped.length} skipped (out of stock)` : ''),
          { duration: 10000 },
        );
      } else {
        toast.success(
          `Plan created: ${res.data.sessions_count} session(s), ${res.data.slips_count} pick slip(s)`
        );
      }
      if (res.data.issues?.length) {
        for (const iss of res.data.issues.slice(0, 5)) toast.warning(iss);
      }
    } catch (e) {
      // Backend may return a structured detail (object) when EVERY cell
      // hit a zero-stock bottleneck — render that as the dialog too.
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.skipped_cells) {
        setPlanResult({
          plan_id: null,
          sessions_count: 0,
          slips_count: 0,
          skipped_cells: detail.skipped_cells,
          qty_adjustments: [],
        });
        setResultDialogOpen(true);
        toast.error(detail.message || 'No combo cell can be fulfilled.', { duration: 10000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create plan');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5] p-6" data-testid="combo-v2-planner-page">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/wms/combo-v2')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-stone-900">Combo V2 — Bulk Planner</h1>
              <p className="text-sm text-stone-500 mt-1">
                Enter assembly qty per size. The system auto-creates sessions and 120-cap pick slips.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/wms/combo-v2/plans')}
              data-testid="view-plans-btn"
            >
              <ListOrdered className="h-4 w-4 mr-1" /> All Plans
            </Button>
          </div>
        </div>

        {savedPlanId && (
          <Card className="p-4 mb-4 bg-green-50 border-green-300">
            <div className="text-green-900 font-medium">
              Plan {savedPlanId} created. Pick slips are now available to SFG Pickers.
            </div>
            <div className="mt-2 flex gap-3">
              <Button
                size="sm"
                onClick={() => navigate(`/wms/combo-v2/plans/${savedPlanId}`)}
                data-testid="view-saved-plan-btn"
              >
                View Plan Details
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setSavedPlanId(null); setPlanName(''); setRows([{ design_name: '', shade: '', description: '', sizes: {}, _query: '', customer_barcode: '', customer_mrp: null, rate_section: null, _barcodeOptions: [], _loadingBarcodes: false }]); }}>
                Start New Plan
              </Button>
            </div>
          </Card>
        )}

        {/* Plan name */}
        <Card className="p-4 mb-4 flex flex-wrap items-start gap-6">
          <div className="flex-1 min-w-[260px] max-w-md">
            <label className="text-sm font-medium text-stone-700">Plan Name (optional)</label>
            <Input
              data-testid="plan-name-input"
              placeholder="e.g. Wedding Combo April Wave 2"
              value={planName}
              onChange={e => setPlanName(e.target.value)}
              className="mt-1"
            />
          </div>
          <PricingChannelSelect value={pricingType} onChange={changeChannel} />
        </Card>

        {/* Matrix */}
        <Card className="overflow-x-auto">
          {loadingOptions ? (
            <div className="p-12 text-center text-stone-500">
              <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading combo master…
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="planner-matrix">
              <thead className="bg-stone-100 sticky top-0">
                <tr>
                  <th className="text-left p-3 border-b font-medium text-stone-700 w-[260px]">Design</th>
                  <th className="text-left p-3 border-b font-medium text-stone-700 w-[140px]">Shade</th>
                  <th className="text-left p-3 border-b font-medium text-stone-700 w-[180px]">Description</th>
                  <th className="text-left p-3 border-b font-medium text-stone-700 w-[200px]">
                    Customer MRP
                  </th>
                  {displayedSizes.map(sz => (
                    <th key={sz} className="p-2 border-b font-medium text-stone-700 w-[64px] text-center" data-testid={`size-header-${sz}`}>
                      {sz}
                    </th>
                  ))}
                  <th className="p-2 border-b font-medium text-stone-700 w-[64px] text-center bg-amber-50">Total</th>
                  <th className="w-[40px] border-b" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const allowed = allowedSizesForRow(r);
                  return (
                    <React.Fragment key={idx}>
                    <tr className="hover:bg-stone-50 border-b" data-testid={`planner-row-${idx}`}>
                      <td className="p-2 align-top relative">
                        <Input
                          ref={el => { inputRefs.current[idx] = el; }}
                          data-testid={`design-input-${idx}`}
                          value={r.design_name}
                          onChange={e => {
                            updateRow(idx, { design_name: e.target.value, _query: e.target.value });
                            openPicker(idx);
                          }}
                          onFocus={() => openPicker(idx)}
                          placeholder="Search design…"
                        />
                      </td>
                      <td className="p-2 align-top">
                        <Input
                          data-testid={`shade-input-${idx}`}
                          value={r.shade}
                          onChange={e => updateRow(idx, { shade: e.target.value })}
                          placeholder="e.g. White"
                        />
                      </td>
                      <td className="p-2 align-top">
                        <Input
                          data-testid={`desc-input-${idx}`}
                          value={r.description}
                          onChange={e => updateRow(idx, { description: e.target.value })}
                          placeholder="Optional"
                        />
                      </td>
                      <td className="p-2 align-top">
                        <MrpSetCell r={r} idx={idx} onSelectMrp={onSelectMrp} onSelectSet={onSelectSet} />
                      </td>
                      {displayedSizes.map(sz => {
                        const isAllowed = !allowed || allowed.has(sz);
                        return (
                          <td key={sz} className={`p-1 text-center ${isAllowed ? '' : 'bg-stone-100'}`}>
                            <Input
                              data-testid={`qty-${idx}-${sz}`}
                              type="number"
                              min={0}
                              disabled={!isAllowed}
                              value={r.sizes?.[sz] ?? ''}
                              onChange={e => setCell(idx, sz, e.target.value)}
                              className="text-center px-1 h-8"
                              placeholder={isAllowed ? '0' : '–'}
                            />
                          </td>
                        );
                      })}
                      <td className="p-2 text-center font-semibold bg-amber-50" data-testid={`row-total-${idx}`}>
                        {rowSum(r)}
                      </td>
                      <td className="p-1 text-center">
                        {rows.length > 1 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeRow(idx)}
                            data-testid={`remove-row-${idx}`}
                          >
                            <Trash2 className="h-4 w-4 text-rose-500" />
                          </Button>
                        )}
                      </td>
                    </tr>
                    {r.design_name && (r._loadingStock || (r._componentStock && Object.keys(r._componentStock).length > 0)) && (
                      <tr className="border-b bg-emerald-50/40" data-testid={`stock-row-${idx}`}>
                        <td colSpan={3} className="p-2 align-top text-[10px] text-stone-500 leading-tight">
                          Component stock &amp; possible qty
                          <div className="text-stone-400 mt-0.5">
                            barcode: avail · <span className="text-rose-500 font-semibold">bottleneck</span> · <span className="font-semibold">*</span> shared
                          </div>
                          {typeof r._rowPossible === 'number' && (
                            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                              <span
                                className="inline-flex items-center gap-1 rounded bg-indigo-100 text-indigo-800 px-1.5 py-0.5 text-[11px] font-bold"
                                data-testid={`row-possible-${idx}`}
                                title="Max combos buildable for this row — shared component pools are split across sizes (unlike the per-size Possible figures)"
                              >
                                Max buildable: {r._rowPossible}
                              </span>
                              <button
                                type="button"
                                onClick={() => fillAllPossible(idx)}
                                data-testid={`fill-all-${idx}`}
                                title="Fill every size with its per-size Possible qty"
                                className="inline-flex items-center gap-1 rounded bg-emerald-600 text-white px-2 py-0.5 text-[11px] font-semibold hover:bg-emerald-700"
                              >
                                Fill all → Possible
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="p-2 align-top text-[10px] text-stone-400">
                          {r._loadingStock ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                        </td>
                        {displayedSizes.map(sz => {
                          const isAllowed = !allowed || allowed.has(sz);
                          const info = r._componentStock?.[sz];
                          if (!isAllowed || !info) {
                            return <td key={sz} className={`p-1 align-top ${isAllowed ? '' : 'bg-stone-100'}`} />;
                          }
                          return (
                            <td key={sz} className="p-1 align-top text-center">
                              <button
                                type="button"
                                onClick={() => setCell(idx, sz, info.possible_qty)}
                                data-testid={`possible-${idx}-${sz}`}
                                title="Click to fill this size's qty"
                                className={
                                  'w-full rounded px-1 py-0.5 text-[11px] font-bold mb-1 transition-colors ' +
                                  (info.possible_qty > 0
                                    ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                                    : 'bg-rose-100 text-rose-700 hover:bg-rose-200')
                                }
                              >
                                {info.possible_qty}
                              </button>
                              <div className="space-y-0.5">
                                {info.components.map(c => (
                                  <div
                                    key={c.component_barcode}
                                    title={`${c.component_name || ''}${c.shared ? ' (shared across sizes)' : ''}`}
                                    data-testid={`comp-${idx}-${sz}-${c.component_barcode}`}
                                    className={
                                      'text-[9px] font-mono leading-tight ' +
                                      (c.is_bottleneck ? 'text-rose-600 font-semibold' : 'text-stone-500')
                                    }
                                  >
                                    {c.component_barcode}: {c.available_pcs}{c.shared ? ' *' : ''}
                                  </div>
                                ))}
                              </div>
                            </td>
                          );
                        })}
                        <td className="bg-amber-50" />
                        <td />
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
                <tr className="bg-amber-50 font-semibold">
                  <td colSpan={4} className="p-3 text-right">Total</td>
                  {displayedSizes.map(sz => (
                    <td key={sz} className="p-2 text-center" data-testid={`col-total-${sz}`}>
                      {totals.perCol[sz]}
                    </td>
                  ))}
                  <td className="p-2 text-center text-base bg-amber-200" data-testid="grand-total">
                    {totals.grand}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          )}
        </Card>

        {/* Actions */}
        <div className="flex justify-between items-center mt-4">
          <Button variant="outline" onClick={addRow} data-testid="add-row-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Row
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || totals.grand === 0}
            data-testid="save-plan-btn"
            className="bg-green-600 hover:bg-green-700"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Generate Plan ({totals.grand} pcs)
          </Button>
        </div>
      </div>
      {/* close picker on outside click */}
      {openPickerIdx !== null && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setOpenPickerIdx(null)}
        />
      )}
      {/* Portal-rendered design picker — escapes the table's overflow clipping */}
      {openPickerIdx !== null && pickerAnchor && createPortal(
        <div
          className="fixed z-50 bg-white border rounded-md shadow-2xl overflow-y-auto"
          style={{
            left: pickerAnchor.left,
            top: pickerAnchor.top,
            width: Math.max(pickerAnchor.width, 360),
            maxHeight: 520,
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          {filteredOptions(rows[openPickerIdx]?._query || rows[openPickerIdx]?.design_name || '').map((opt, i) => (
            <button
              key={i}
              type="button"
              className="w-full text-left px-3 py-2.5 text-xs hover:bg-amber-50 border-b last:border-0"
              onClick={() => pickOption(openPickerIdx, opt)}
              data-testid={`option-${openPickerIdx}-${i}`}
            >
              <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                <span>{opt.design_name}</span>
                {opt.description ? (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wide"
                    data-testid={`option-${openPickerIdx}-${i}-desc`}
                  >
                    {opt.description}
                  </span>
                ) : null}
              </div>
              <div className="text-stone-500 text-[11px] mt-0.5">
                {opt.shade} • sizes: {(opt.sizes_available || []).join(', ')}
              </div>
            </button>
          ))}
          {!filteredOptions(rows[openPickerIdx]?._query || rows[openPickerIdx]?.design_name || '').length && (
            <div className="p-3 text-stone-400 text-xs">No matches</div>
          )}
        </div>,
        document.body
      )}

      {/* 2026-05-13 — Stock-aware Planner Result Dialog. Surfaces qty
          adjustments + skipped combos with bottleneck component info so
          the operator immediately sees which combos couldn't run and
          which component is short. */}
      <Dialog open={resultDialogOpen} onOpenChange={setResultDialogOpen}>
        <DialogContent className="max-w-3xl" data-testid="planner-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-amber-600" />
              {planResult?.plan_id ? 'Plan created — stock-aware adjustments applied' : 'No plan created — no combo cell can be fulfilled'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 text-sm">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-emerald-50 rounded border border-emerald-200 text-center">
                <div className="text-xs text-emerald-700">Sessions</div>
                <div className="text-2xl font-bold text-emerald-800">
                  {planResult?.sessions_count || 0}
                </div>
              </div>
              <div className="p-3 bg-blue-50 rounded border border-blue-200 text-center">
                <div className="text-xs text-blue-700">Pick Slips</div>
                <div className="text-2xl font-bold text-blue-800">
                  {planResult?.slips_count || 0}
                </div>
              </div>
              <div className="p-3 bg-amber-50 rounded border border-amber-200 text-center">
                <div className="text-xs text-amber-700">Adjusted / Skipped</div>
                <div className="text-2xl font-bold text-amber-800">
                  {(planResult?.qty_adjustments?.length || 0) + (planResult?.skipped_cells?.length || 0)}
                </div>
              </div>
            </div>

            {planResult?.qty_adjustments?.length > 0 && (
              <div data-testid="planner-result-adjustments">
                <div className="flex items-center gap-1 text-amber-800 font-semibold mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  Qty Adjustments ({planResult.qty_adjustments.length})
                </div>
                <div className="text-xs text-stone-600 mb-2">
                  These cells were capped to the achievable combo count based on the bottleneck component's stock.
                </div>
                <div className="border border-amber-200 rounded max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-amber-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">Combo</th>
                        <th className="px-2 py-1 text-right">Requested</th>
                        <th className="px-2 py-1 text-right">Achievable</th>
                        <th className="px-2 py-1 text-left">Bottleneck</th>
                      </tr>
                    </thead>
                    <tbody>
                      {planResult.qty_adjustments.map((adj, i) => (
                        <tr key={i} className="border-t border-amber-100">
                          <td className="px-2 py-1 font-mono">
                            {adj.design_name} / {adj.shade} / {adj.size}
                          </td>
                          <td className="px-2 py-1 text-right line-through text-stone-500">{adj.requested_qty}</td>
                          <td className="px-2 py-1 text-right font-bold text-amber-800">{adj.adjusted_qty}</td>
                          <td className="px-2 py-1 text-stone-600">
                            {adj.bottleneck ? (
                              <span className="inline-flex items-center gap-2">
                                <span>{`${adj.bottleneck.component_barcode} (${adj.bottleneck.available_pcs} pcs, ${adj.bottleneck.per_combo}/combo)`}</span>
                                <button
                                  type="button"
                                  data-testid={`planner-diagnose-adj-${i}`}
                                  onClick={() => openDiagnostic(adj.bottleneck.component_barcode)}
                                  className="text-[10px] text-amber-800 underline hover:text-amber-900"
                                  title="Inspect stock & release orphan allocations"
                                >
                                  Diagnose
                                </button>
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {planResult?.skipped_cells?.length > 0 && (
              <div data-testid="planner-result-skipped">
                <div className="flex items-center gap-1 text-rose-800 font-semibold mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  Skipped — 0 stock ({planResult.skipped_cells.length})
                </div>
                <div className="text-xs text-stone-600 mb-2">
                  These combos couldn't run because one component had no stock. The other planned combos were still created.
                </div>
                <div className="border border-rose-200 rounded max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-rose-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">Combo</th>
                        <th className="px-2 py-1 text-right">Requested</th>
                        <th className="px-2 py-1 text-left">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {planResult.skipped_cells.map((sk, i) => (
                        <tr key={i} className="border-t border-rose-100">
                          <td className="px-2 py-1 font-mono">
                            {sk.design_name} / {sk.shade} / {sk.size}
                          </td>
                          <td className="px-2 py-1 text-right text-stone-500">{sk.requested_qty}</td>
                          <td className="px-2 py-1 text-rose-700">
                            <span>{sk.reason}</span>
                            {sk.bottleneck?.component_barcode && (
                              <button
                                type="button"
                                data-testid={`planner-diagnose-skip-${i}`}
                                onClick={() => openDiagnostic(sk.bottleneck.component_barcode)}
                                className="ml-2 text-[10px] text-rose-800 underline hover:text-rose-900"
                                title="Inspect stock & release orphan allocations"
                              >
                                Diagnose
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setResultDialogOpen(false)} data-testid="planner-result-close">
              Close
            </Button>
            {planResult?.plan_id && (
              <Button
                className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                onClick={() => {
                  setResultDialogOpen(false);
                  navigate(`/wms/combo-v2/plans/${planResult.plan_id}`);
                }}
              >
                Open Plan
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-05-15 — Stock Diagnostic Dialog. Investigates why the matrix
          planner sees less stock than physical reality, and offers a
          one-click heal to release orphan ALLOCATED bundles back to
          STORED. Admin-only on the backend. */}
      <Dialog open={diagOpen} onOpenChange={setDiagOpen}>
        <DialogContent className="max-w-2xl" data-testid="planner-diagnose-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-stone-700" />
              Stock Diagnostic — {diagSku}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {diagLoading && (
              <div className="flex items-center gap-2 text-stone-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading diagnostic…
              </div>
            )}
            {!diagLoading && diagData && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded border border-emerald-200 bg-emerald-50 p-2">
                    <div className="text-[10px] uppercase tracking-wide text-emerald-800">
                      Visible to planner
                    </div>
                    <div
                      className="text-xl font-bold text-emerald-900"
                      data-testid="diag-visible-pcs"
                    >
                      {diagData.visible_to_planner_pcs}
                    </div>
                    <div className="text-[10px] text-emerald-700">pcs (STORED + PENDING_PUTAWAY)</div>
                  </div>
                  <div className="rounded border border-rose-200 bg-rose-50 p-2">
                    <div className="text-[10px] uppercase tracking-wide text-rose-800">Hidden</div>
                    <div className="text-xl font-bold text-rose-900" data-testid="diag-hidden-pcs">
                      {diagData.hidden_pcs}
                    </div>
                    <div className="text-[10px] text-rose-700">pcs (other statuses)</div>
                  </div>
                  <div className="rounded border border-amber-200 bg-amber-50 p-2">
                    <div className="text-[10px] uppercase tracking-wide text-amber-800">Healable</div>
                    <div
                      className="text-xl font-bold text-amber-900"
                      data-testid="diag-healable-pcs"
                    >
                      {diagData.healable_pcs}
                    </div>
                    <div className="text-[10px] text-amber-700">pcs (orphan allocations)</div>
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-stone-700 mb-1">By Status</div>
                  <div className="border border-stone-200 rounded text-xs">
                    <table className="w-full">
                      <thead className="bg-stone-50">
                        <tr>
                          <th className="px-2 py-1 text-left">Status</th>
                          <th className="px-2 py-1 text-right">Bundles</th>
                          <th className="px-2 py-1 text-right">Pcs</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(diagData.by_status || {}).map(([st, v]) => (
                          <tr key={st} className="border-t border-stone-100">
                            <td className="px-2 py-1 font-mono">{st}</td>
                            <td className="px-2 py-1 text-right">{v.bundles}</td>
                            <td className="px-2 py-1 text-right">{v.pcs}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="text-xs text-stone-600 italic">{diagData.hint}</div>

                {diagData.sample_rows?.length > 0 && (
                  <details>
                    <summary className="cursor-pointer text-xs text-stone-600 hover:text-stone-800">
                      Sample bundles (first {diagData.sample_rows.length})
                    </summary>
                    <div className="mt-2 max-h-40 overflow-y-auto border border-stone-200 rounded text-[11px]">
                      <table className="w-full">
                        <thead className="bg-stone-50 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left">Bundle</th>
                            <th className="px-2 py-1 text-left">Status</th>
                            <th className="px-2 py-1 text-left">Bin</th>
                            <th className="px-2 py-1 text-right">Pcs</th>
                            <th className="px-2 py-1 text-left">Slip / Plan</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diagData.sample_rows.map((r, i) => (
                            <tr key={i} className="border-t border-stone-100">
                              <td className="px-2 py-1 font-mono">{r.bundle_code}</td>
                              <td className="px-2 py-1">{r.status}</td>
                              <td className="px-2 py-1">{r.bin_location || '-'}</td>
                              <td className="px-2 py-1 text-right">{r.avail_pcs}</td>
                              <td className="px-2 py-1 font-mono text-[10px]">
                                {r.allocated_to_slip || r.picked_for_v2_plan || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDiagOpen(false)}
              data-testid="diag-close-btn"
            >
              Close
            </Button>
            {diagData?.healable_pcs > 0 && (
              <>
                <Button
                  variant="outline"
                  disabled={diagHealing}
                  onClick={() => runHeal(true)}
                  data-testid="diag-dry-run-btn"
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                >
                  {diagHealing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                  Dry-run
                </Button>
                <Button
                  disabled={diagHealing}
                  onClick={() => runHeal(false)}
                  data-testid="diag-heal-btn"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {diagHealing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                  Release {diagData.healable_pcs} pcs → STORED
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
